export class CookieSetError extends Error {
  constructor(error: unknown) {
    super()
    this.name = 'CookieSetError'
    this.stack = (error as Error).stack
    this.message = 'Cookies cannot be modified in this context'
  }
}

const PARTIAL_NEXT_CANT_MODIFY_COOKIES_ERROR_MESSAGE =
  'Cookies can only be modified in'

const MAX_CAUSE_DEPTH = 3

function collectErrorMessages(error: unknown, depth = 0): string[] {
  if (error === null || error === undefined || depth > MAX_CAUSE_DEPTH) {
    return []
  }

  if (typeof error === 'string') {
    return [error]
  }

  if (typeof error === 'object') {
    const messages: string[] = []
    const message = (error as { message?: unknown }).message

    if (typeof message === 'string' && message.length > 0) {
      messages.push(message)
    }

    const cause = (error as { cause?: unknown }).cause
    if (cause !== null && cause !== undefined) {
      messages.push(...collectErrorMessages(cause, depth + 1))
    }

    if (messages.length === 0) {
      messages.push(String(error))
    }

    return messages
  }

  return [String(error)]
}

export function getErrorMessage(error: unknown): string {
  return collectErrorMessages(error)[0] ?? ''
}

/**
 * This method helps us know if an error occurred when trying to set a cookie.
 *
 * Checks the message string directly (not just `instanceof Error`) because
 * Vercel's serverless runtime can throw errors from a different JS realm where
 * `instanceof Error` returns false.
 */
export function isCannotSetCookieError(error: unknown): boolean {
  if (error instanceof CookieSetError) {
    return true
  }

  return collectErrorMessages(error).some((message) =>
    message.includes(PARTIAL_NEXT_CANT_MODIFY_COOKIES_ERROR_MESSAGE),
  )
}
